import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_URL, apiClient, getSessionTokenHeaders } from '../client'
import { BASE_URL } from './shared'
import type {
  EnrollmentHeartbeatRequest,
  EnrollmentStepSubmissionData,
  EnrollmentStepSubmissionResponse,
  EnrollmentSubmitResult,
  EnrollmentUserProfile,
  PricingQuoteRequest,
  PricingQuoteResponse,
  PublicEnrollmentStartRequest,
  ResumedEnrollment,
  SignaturePrepareResult,
  UpdateEnrollmentEndorsementsRequest
} from './types'

export const submitEnrollmentStepRequest = async ({
  sessionToken,
  data
}: {
  sessionToken: string
  data: EnrollmentStepSubmissionData
}) => {
  const response = await apiClient.put<EnrollmentStepSubmissionResponse>(
    `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/step`,
    {
      ...data,
      sessionToken
    },
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )

  return response.data
}

export const submitEnrollmentHeartbeatRequest = async ({
  sessionToken,
  data
}: {
  sessionToken: string
  data: EnrollmentHeartbeatRequest
}) => {
  await apiClient.post<void>(`${BASE_URL}/public/${sessionToken}/heartbeat`, data, {
    headers: getSessionTokenHeaders(sessionToken)
  })
}

export const sendEnrollmentHeartbeatBeacon = (
  sessionToken: string,
  data: EnrollmentHeartbeatRequest
) => {
  if (typeof window === 'undefined') {
    return
  }

  const url = `${API_URL}${BASE_URL}/public/${encodeURIComponent(sessionToken)}/heartbeat`
  const body = JSON.stringify(data)

  void fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getSessionTokenHeaders(sessionToken)
    },
    body,
    keepalive: true
  }).catch(() => {})
}

export const updateEnrollmentEndorsementsRequest = async ({
  sessionToken,
  endorsements
}: UpdateEnrollmentEndorsementsRequest) => {
  const response = await apiClient.put<string[]>(
    `${BASE_URL}/session/${sessionToken}/endorsements`,
    { endorsements },
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )

  return response.data
}

export const createEnrollmentQuoteRequest = async ({
  sessionToken,
  data
}: {
  sessionToken: string
  data: PricingQuoteRequest
}) => {
  const response = await apiClient.post<PricingQuoteResponse>(
    `${BASE_URL}/session/${sessionToken}/quote`,
    data,
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )

  return response.data
}

export const recoverEnrollment = async (email: string): Promise<string | null> => {
  const response = await apiClient.post<{ recoveryToken?: string; token?: string }>(
    `${BASE_URL}/recover`,
    { email }
  )
  return response.data?.recoveryToken ?? response.data?.token ?? null
}

export const resumeEnrollment = async (recoveryToken: string) => {
  const response = await apiClient.get<{ message?: string; enrollment?: ResumedEnrollment }>(
    `${BASE_URL}/resume/${encodeURIComponent(recoveryToken)}`
  )
  return response.data
}

export const startPublicEnrollmentRequest = async (data: PublicEnrollmentStartRequest) => {
  const response = await apiClient.post<EnrollmentStepSubmissionResponse>(
    `${BASE_URL}/public/start`,
    data
  )

  return response.data
}

export const getCurrentUserRecoveryState = async () => {
  const response = await apiClient.get<{ message?: string; enrollment?: ResumedEnrollment }>(
    `${BASE_URL}/me/recovery-state`
  )
  return response.data
}

export const getCurrentUserProfileRequest = async (sessionToken?: string | null) => {
  const encodedSessionToken = sessionToken ? encodeURIComponent(sessionToken) : ''
  const response = await apiClient.get<EnrollmentUserProfile>(
    `${BASE_URL}/session/${encodedSessionToken}/profile`,
    {
      headers: sessionToken ? getSessionTokenHeaders(sessionToken) : undefined
    }
  )
  return response.data
}

export const prepareEnrollmentSignatureRequest = async (sessionToken: string) => {
  const response = await apiClient.post<SignaturePrepareResult>(
    `${BASE_URL}/signature/prepare/${encodeURIComponent(sessionToken)}`,
    undefined,
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )
  return response.data
}

export const submitFinalEnrollmentRequest = async (
  sessionToken: string,
  data?: {
    primaryPaymentType?: 'GOV_ASSISTANCE' | 'AFFIRM_LINK' | 'Self-Pay'
  }
) => {
  const response = await apiClient.post<EnrollmentSubmitResult>(
    `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/submit`,
    data ?? {},
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )
  return response.data
}

export const getPublicEnrollmentStateRequest = async (sessionToken: string) => {
  const response = await apiClient.get<EnrollmentStepSubmissionResponse>(
    `${BASE_URL}/session/${encodeURIComponent(sessionToken)}`,
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )
  return response.data
}

export const useGetPublicEnrollment = (sessionToken: string | null) => {
  return useQuery({
    queryKey: ['enrollment', 'public', sessionToken],
    queryFn: () => getPublicEnrollmentStateRequest(sessionToken!),
    enabled: !!sessionToken
  })
}

export const useStartSelfEnrollment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: startPublicEnrollmentRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment'] })
    }
  })
}

export const useSubmitEnrollmentStep = () => {
  return useMutation({
    mutationFn: submitEnrollmentStepRequest
  })
}

export const useSubmitFinalEnrollment = () => {
  return useMutation({
    mutationFn: (sessionToken: string) => submitFinalEnrollmentRequest(sessionToken)
  })
}

export const useUpdateEnrollmentEndorsements = () => {
  return useMutation({
    mutationFn: updateEnrollmentEndorsementsRequest
  })
}

export const useEnrollmentQuote = (
  sessionToken: string | null,
  data: PricingQuoteRequest | null
) => {
  return useQuery({
    queryKey: [
      'enrollment',
      'quote',
      sessionToken,
      data?.programExternalId ?? null,
      data?.locationExternalId ?? null,
      data?.enrollmentPath ?? null,
      data?.discountCode ?? null,
      data?.endorsements ?? []
    ],
    queryFn: () => createEnrollmentQuoteRequest({ sessionToken: sessionToken!, data: data! }),
    enabled: false,
    retry: false
  })
}
